// Component: BacktestEquityCurve.jsx
// Purpose: Equity curve chart with zoom controls and optional timeline sync with main chart
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiMaximize2, FiZoomIn, FiZoomOut, FiLink, FiLink2 } from 'react-icons/fi';
import { ChartSyncService } from '../../../../../components/SmartChart/utils';

const asNumber = (value) => {
    if (value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const toEpochSeconds = (rawTime) => {
    if (rawTime == null) return null;
    if (typeof rawTime === 'number') {
        if (!Number.isFinite(rawTime)) return null;
        return rawTime > 1e12 ? Math.floor(rawTime / 1000) : Math.floor(rawTime);
    }
    const ms = new Date(rawTime).getTime();
    if (!Number.isFinite(ms)) return null;
    return Math.floor(ms / 1000);
};

const normalizeEquityCurve = (points = []) => {
    if (!Array.isArray(points)) return [];
    const deduped = new Map();
    points.forEach((point) => {
        const time = toEpochSeconds(point?.time ?? point?.timestamp ?? point?.ts);
        const value = asNumber(point?.equity ?? point?.value ?? point?.balance);
        if (time == null || value == null) return;
        deduped.set(time, value);
    });
    return Array.from(deduped.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([time, value]) => ({ time, value }));
};

// --> strategyId: when provided, shows Sync button that links equity curve timeline with main OHLC chart
//     Uses own ChartSyncService instance over BroadcastChannel — no prop-chain ref needed.
const BacktestEquityCurve = ({ data, buyHoldData, initialCapital = null, heightClass = 'h-80', strategyId = null }) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const pointsRef = useRef([]);
    const baseEquityRef = useRef(null);
    const isSyncingRef = useRef(false);  // Prevent sync feedback loops
    const syncSvcRef = useRef(null);     // Own ChartSyncService instance

    const [syncEnabled, setSyncEnabled] = useState(false);
    const [showBuyHold, setShowBuyHold] = useState(false);
    const buyHoldSeriesRef = useRef(null);
    const [legendData, setLegendData] = useState(null);

    // --> Create/destroy own ChartSyncService when strategyId changes
    useEffect(() => {
        if (!strategyId) return undefined;
        const svc = new ChartSyncService(strategyId);
        syncSvcRef.current = svc;
        return () => {
            svc.destroy?.();
            syncSvcRef.current = null;
            setSyncEnabled(false);
        };
    }, [strategyId]);

    const points = useMemo(() => normalizeEquityCurve(data), [data]);
    const buyHoldPoints = useMemo(() => normalizeEquityCurve(buyHoldData || []), [buyHoldData]);
    const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

    const currentEquity = points.length > 0 ? points[points.length - 1].value : null;
    const baseEquity = asNumber(initialCapital) ?? (points.length > 0 ? points[0].value : null);
    const netPnl = currentEquity != null && baseEquity != null ? currentEquity - baseEquity : null;
    const netPnlPct = netPnl != null && baseEquity ? (netPnl / baseEquity) * 100 : null;

    // --> Zoom controls
    const handleFitAll = useCallback(() => {
        chartRef.current?.timeScale().fitContent();
    }, []);

    const handleZoomIn = useCallback(() => {
        const ts = chartRef.current?.timeScale();
        if (!ts) return;
        const range = ts.getVisibleLogicalRange();
        if (!range) return;
        const mid = (range.from + range.to) / 2;
        const half = (range.to - range.from) / 4;
        ts.setVisibleLogicalRange({ from: mid - half, to: mid + half });
    }, []);

    const handleZoomOut = useCallback(() => {
        const ts = chartRef.current?.timeScale();
        if (!ts) return;
        const range = ts.getVisibleLogicalRange();
        if (!range) return;
        const mid = (range.from + range.to) / 2;
        const half = (range.to - range.from);
        ts.setVisibleLogicalRange({ from: mid - half, to: mid + half });
    }, []);

    // --> Update chart data when points change
    useEffect(() => {
        pointsRef.current = points;
        if (!seriesRef.current) return;
        seriesRef.current.setData(points);
        if (chartRef.current && points.length > 1) {
            chartRef.current.timeScale().fitContent();
        }
    }, [points]);

    // --> Update buy & hold series data when buyHoldPoints change
    useEffect(() => {
        if (buyHoldSeriesRef.current && buyHoldPoints.length > 0) {
            buyHoldSeriesRef.current.setData(buyHoldPoints);
        }
    }, [buyHoldPoints]);

    // --> Toggle buy & hold series visibility
    useEffect(() => {
        if (buyHoldSeriesRef.current) {
            buyHoldSeriesRef.current.applyOptions({ visible: showBuyHold });
        }
    }, [showBuyHold]);

    // --> Update baseline when initialCapital changes
    useEffect(() => {
        if (!seriesRef.current || baseEquity == null) return;
        baseEquityRef.current = baseEquity;
        seriesRef.current.applyOptions({
            baseValue: { type: 'price', price: baseEquity },
        });
    }, [baseEquity]);

    // --> Timeline sync with main chart
    useEffect(() => {
        const svc = syncSvcRef.current;
        if (!syncEnabled || !svc || !chartRef.current) return undefined;

        // Subscribe to range changes from main chart (via BroadcastChannel)
        const unsubscribe = svc.subscribeRange((range) => {
            if (isSyncingRef.current || !chartRef.current || !range) return;
            const current = chartRef.current.timeScale().getVisibleLogicalRange();
            if (
                !current ||
                Math.abs(current.from - range.from) > 0.001 ||
                Math.abs(current.to - range.to) > 0.001
            ) {
                isSyncingRef.current = true;
                try {
                    chartRef.current.timeScale().setVisibleLogicalRange(range);
                } finally {
                    isSyncingRef.current = false;
                }
            }
        });

        // Emit our range changes to the main chart
        const emitOurRange = () => {
            if (isSyncingRef.current || !chartRef.current) return;
            const range = chartRef.current.timeScale().getVisibleLogicalRange();
            if (range) {
                isSyncingRef.current = true;
                try {
                    svc.emitRange(range);
                } finally {
                    isSyncingRef.current = false;
                }
            }
        };

        chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(emitOurRange);

        return () => {
            unsubscribe?.();
            chartRef.current?.timeScale().unsubscribeVisibleLogicalRangeChange(emitOurRange);
        };
    }, [syncEnabled]);

    // --> Initialize chart once (re-init on dark mode toggle)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        let isCancelled = false;

        const initChart = async () => {
            let chartingLib;
            try {
                chartingLib = await import('lightweight-charts');
            } catch (error) {
                console.error('[BacktestEquityCurve] Failed to load lightweight-charts:', error);
                return;
            }

            const { createChart, BaselineSeries, ColorType, CrosshairMode } = chartingLib;
            if (isCancelled || !containerRef.current) return;

            // --> Read DS tokens so the canvas chart matches its card in both themes
            const rootStyle = getComputedStyle(document.documentElement);
            const cvar = (name, fallback) => rootStyle.getPropertyValue(name).trim() || fallback;
            const chartBg = cvar('--bg-2', isDarkMode ? '#1a1a1a' : '#ffffff');
            const chartText = cvar('--fg-3', isDarkMode ? '#9CA3AF' : '#6B7280');
            const chartGrid = cvar('--divider', isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(226,232,240,0.8)');
            const chartBorder = cvar('--border', isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(209,213,219,0.8)');
            const chart = createChart(el, {
                width: el.clientWidth,
                height: el.clientHeight,
                layout: {
                    background: { type: ColorType.Solid, color: chartBg },
                    textColor: chartText,
                    attributionLogo: false,
                },
                watermark: { visible: false },
                grid: {
                    vertLines: { color: chartGrid },
                    horzLines: { color: chartGrid },
                },
                crosshair: { mode: CrosshairMode.Normal },
                rightPriceScale: {
                    borderColor: chartBorder,
                },
                timeScale: {
                    borderColor: chartBorder,
                    timeVisible: true,
                    secondsVisible: false,
                    rightOffset: 2,
                    // --> Enable full mouse/touch interaction
                    lockVisibleTimeRangeOnResize: false,
                },
                // --> Explicitly allow scroll and scale
                handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
                handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
            });

            const baseline = baseEquityRef.current ?? (pointsRef.current[0]?.value ?? 0);
            const series = chart.addSeries(BaselineSeries, {
                baseValue: { type: 'price', price: baseline },
                lineWidth: 2,
                topLineColor: 'rgba(34, 197, 94, 1)',
                topFillColor1: 'rgba(34, 197, 94, 0.28)',
                topFillColor2: 'rgba(34, 197, 94, 0.02)',
                bottomLineColor: 'rgba(239, 68, 68, 1)',
                bottomFillColor1: 'rgba(239, 68, 68, 0.02)',
                bottomFillColor2: 'rgba(239, 68, 68, 0.28)',
                priceLineVisible: false,
                lastValueVisible: true,
            });

            chartRef.current = chart;
            seriesRef.current = series;

            //-- Buy & Hold line series (hidden by default)
            const { LineSeries } = chartingLib;
            const bhSeries = chart.addSeries(LineSeries, {
                color: 'rgba(99, 102, 241, 0.7)',   // Indigo — distinct from green/red equity
                lineWidth: 1.5,
                lineStyle: 0,  // Solid for clarity at scale
                crosshairMarkerVisible: true,
                crosshairMarkerRadius: 3,
                priceLineVisible: false,
                lastValueVisible: true,
                visible: false,
            });
            buyHoldSeriesRef.current = bhSeries;

            //-- Crosshair move subscription for floating legend
            chart.subscribeCrosshairMove(param => {
                if (!param.time || !param.seriesData) {
                    setLegendData(null);
                    return;
                }
                const stratVal = param.seriesData.get(seriesRef.current);
                const bhVal = param.seriesData.get(buyHoldSeriesRef.current);
                setLegendData({
                    strategy: stratVal?.value ?? null,
                    buyHold: bhVal?.value ?? null,
                });
            });

            if (typeof ResizeObserver !== 'undefined') {
                resizeObserverRef.current = new ResizeObserver((entries) => {
                    const entry = entries[0];
                    if (!entry || !chartRef.current) return;
                    const { width, height } = entry.contentRect;
                    if (width > 0 && height > 0) {
                        chartRef.current.applyOptions({ width, height });
                    }
                });
                resizeObserverRef.current.observe(el);
            }

            if (pointsRef.current.length > 0) {
                series.setData(pointsRef.current);
                chart.timeScale().fitContent();
            }

            // --> Set buy & hold data if already available at init time
            if (buyHoldPoints.length > 0) {
                bhSeries.setData(buyHoldPoints);
            }
        };

        initChart();

        return () => {
            isCancelled = true;
            resizeObserverRef.current?.disconnect();
            resizeObserverRef.current = null;
            chartRef.current?.remove();
            chartRef.current = null;
            seriesRef.current = null;
            buyHoldSeriesRef.current = null;
        };
    }, [isDarkMode]);

    if (points.length === 0) {
        return (
            <div className={`${heightClass} flex items-center justify-center text-fg-3 border border-dashed border-[var(--divider)] rounded-xl bg-bg-3/50`}>
                <span className="text-sm font-medium">No equity curve data available.</span>
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* --> Stats + Controls bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                {/* Stats */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="px-2.5 py-1 rounded-lg bg-bg-1 border border-[var(--divider)]">
                        <span className="text-[9px] uppercase tracking-wider text-fg-3 font-bold block">Current Equity</span>
                        <span className="text-xs font-mono font-bold text-fg-1">
                            ₹{(currentEquity || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </span>
                    </div>
                    {netPnl != null && (
                        <div className="px-2.5 py-1 rounded-lg bg-bg-1 border border-[var(--divider)]">
                            <span className="text-[9px] uppercase tracking-wider text-fg-3 font-bold block">Net P&amp;L</span>
                            <span className={`text-xs font-mono font-bold ${netPnl >= 0 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]'}`}>
                                {netPnl >= 0 ? '+' : ''}₹{netPnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                {netPnlPct != null ? <span className="text-[10px] ml-1 opacity-75">({netPnlPct >= 0 ? '+' : ''}{netPnlPct.toFixed(2)}%)</span> : null}
                            </span>
                        </div>
                    )}
                </div>

                {/* Zoom + Sync controls */}
                <div className="flex items-center gap-2">
                    {/* --> Buy & Hold toggle */}
                    <button
                        onClick={() => buyHoldPoints.length && setShowBuyHold(v => !v)}
                        disabled={!buyHoldPoints.length}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold transition-colors border ${
 showBuyHold
                                ? 'bg-bg-3 text-white border-[var(--border)] shadow-sm'
                                : buyHoldPoints.length
                                    ? 'text-fg-2 border-[var(--divider)] hover:border-[var(--divider)] hover:text-fg-3'
                                    : 'text-fg-3 border-[var(--divider)] cursor-not-allowed'
                        }`}
                        title={showBuyHold ? 'Hide buy & hold benchmark' : 'Show buy & hold benchmark overlay'}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0">
                            <path d="M1 8L4 5L7 7L11 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Buy &amp; Hold
                    </button>
                    <button
                        onClick={handleZoomIn}
                        title="Zoom In"
                        className="p-1.5 rounded-lg text-fg-2 hover:text-[var(--node-visual)] hover:bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] transition-colors"
                    >
                        <FiZoomIn size={14} />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        title="Zoom Out"
                        className="p-1.5 rounded-lg text-fg-2 hover:text-[var(--node-visual)] hover:bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] transition-colors"
                    >
                        <FiZoomOut size={14} />
                    </button>
                    <button
                        onClick={handleFitAll}
                        title="Fit full timeline"
                        className="p-1.5 rounded-lg text-fg-2 hover:text-[var(--node-visual)] hover:bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] transition-colors"
                    >
                        <FiMaximize2 size={14} />
                    </button>
                    {strategyId && (
                        <button
                            onClick={() => setSyncEnabled(v => !v)}
                            title={syncEnabled ? 'Synced with main chart — click to unsync' : 'Sync timeline with main chart'}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-colors border ${
 syncEnabled
                                    ? 'bg-[var(--node-visual)] text-white border-[var(--node-visual)] shadow-sm'
                                    : 'text-fg-2 border-[var(--divider)] hover:border-[var(--node-visual)] hover:text-[var(--node-visual)]'
                            }`}
                        >
                            {syncEnabled ? <FiLink size={11} /> : <FiLink2 size={11} />}
                            Sync
                        </button>
                    )}
                </div>
            </div>

            <div className={`${heightClass} relative rounded-xl border border-[var(--divider)] overflow-hidden`}>
                <div
                    ref={containerRef}
                    className="w-full h-full"
                />
                {showBuyHold && legendData && (
                    <div className="absolute top-2 left-2 z-10 bg-bg-1/85 backdrop-blur-sm rounded-lg px-3 py-2 pointer-events-none border border-[var(--divider)]">
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className="w-2.5 h-0.5 rounded-full bg-[var(--color-profit)] shrink-0"></span>
                            <span className="text-[10px] text-fg-3">Strategy</span>
                            <span className="text-xs font-mono font-bold text-[var(--color-profit)] ml-auto pl-3">
                                ₹{legendData.strategy?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || '—'}
                            </span>
                            {legendData.strategy != null && baseEquity != null && baseEquity > 0 && (
                                <span className={`text-[9px] font-mono ${legendData.strategy >= baseEquity ? 'text-profit/70' : 'text-loss/70'}`}>
                                    ({((legendData.strategy - baseEquity) / baseEquity * 100).toFixed(1)}%)
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-0.5 rounded-full bg-[var(--node-comparator)] shrink-0"></span>
                            <span className="text-[10px] text-fg-3">Buy &amp; Hold</span>
                            <span className="text-xs font-mono font-bold text-[var(--node-comparator)] ml-auto pl-3">
                                ₹{legendData.buyHold?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || '—'}
                            </span>
                            {legendData.buyHold != null && baseEquity != null && baseEquity > 0 && (
                                <span className={`text-[9px] font-mono ${legendData.buyHold >= baseEquity ? 'text-profit/70' : 'text-loss/70'}`}>
                                    ({((legendData.buyHold - baseEquity) / baseEquity * 100).toFixed(1)}%)
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <p className="text-[9px] text-fg-3 text-center mt-1.5">
                Scroll to zoom · Drag to pan
            </p>
        </div>
    );
};

export default BacktestEquityCurve;
